import React, { useEffect } from "react"
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Refresh, Undo } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { estornarPixPagarme } from "../../../services/extratoPagarmeFornecedor"
import type { TransacaoPagarmeModel } from "../../../services/extratoPagarmeFornecedor"
import { ExtratoPagarmeFornecedorActionsTypes } from "../context/extratoPagarmeFornecedorActions"
import { useExtratoPagarmeFornecedorContext } from "../context/useExtratoPagarmeFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    filtroRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    saldoBox: {
        background: "linear-gradient(135deg, #1a3c5e 0%, #2c6e49 100%)",
        borderRadius: 15,
        padding: theme.spacing(2, 3),
        color: "white",
        display: "inline-block",
    },
    emptyText: {
        color: "#7f8c8d",
        fontStyle: "italic",
        padding: theme.spacing(2),
    },
    thFinanceiro: {
        backgroundColor: "#eef4ff",
    },
    valorPositivo: {
        color: "#1cc88a",
        fontWeight: 600,
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}))

export function ExtratoPagarmeFornecedorView() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento, carregarExtrato } = useExtratoPagarmeFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handlePesquisar = () => {
        if (!dados.dataInicio || !dados.dataFim) {
            alertStart("Informe as datas de início e fim.", "error")
            return
        }
        if (dados.dataInicio > dados.dataFim) {
            alertStart("A data de início não pode ser maior que a data final.", "error")
            return
        }
        const inicio = new Date(dados.dataInicio)
        const fim = new Date(dados.dataFim)
        const diffDias = (fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)
        if (diffDias > 100) {
            alertStart("O intervalo não pode exceder 100 dias.", "error")
            return
        }
        carregarExtrato()
    }

    const handleEstornar = async (transacao: TransacaoPagarmeModel) => {
        if (!window.confirm("Tem certeza que deseja estornar este pagamento?")) return
        const autorizacao = localStorage.getItem("chaveSistema") ?? ""
        const res = await estornarPixPagarme(autorizacao, transacao.id)
        if ("retornoErro" in res) {
            alertStart(res.mensagem || "Erro ao estornar", "error")
        } else {
            alertStart("Estornado com sucesso", "success")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#2c3e50", marginBottom: 4 }}>
                    Extrato de Vendas Pix (Pagarme)
                </Typography>
                <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 24 }}>
                    Visualize o extrato detalhado das suas vendas via Pix processadas pela Pagarme.
                </Typography>

                {/* Filtro */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardHeaderTitle}>Período</Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filtroRow}>
                            <TextField
                                label="Data início"
                                type="date"
                                size="small"
                                variant="outlined"
                                value={dados.dataInicio}
                                onChange={e => {
                                    const value = e.target.value
                                    dispatch({
                                        type: ExtratoPagarmeFornecedorActionsTypes.SET_DATA_INICIO,
                                        payload: value,
                                    })
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Data fim"
                                type="date"
                                size="small"
                                variant="outlined"
                                value={dados.dataFim}
                                onChange={e => {
                                    const value = e.target.value
                                    dispatch({
                                        type: ExtratoPagarmeFornecedorActionsTypes.SET_DATA_FIM,
                                        payload: value,
                                    })
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "#4caf50", color: "white", borderRadius: 25 }}
                                startIcon={<Refresh />}
                                onClick={handlePesquisar}
                            >
                                Atualizar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Saldo */}
                {dados.saldo && (
                    <Box mb={3}>
                        <Box className={classes.saldoBox}>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                Saldo disponível: R$ {dados.saldo}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Tabela de transações */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardHeaderTitle}>Transações</Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.transacoes.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                A pesquisa não retornou resultados.
                            </Typography>
                        ) : (
                            <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ minWidth: 930, width: "100%" }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 120 }}>ID</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Operação</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Situação</TableCell>
                                            <TableCell style={{ minWidth: 100 }} className={classes.thFinanceiro} align="right">Líquido</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Dt. Cadastro</TableCell>
                                            <TableCell style={{ minWidth: 90 }}>ID Pedido</TableCell>
                                            <TableCell style={{ minWidth: 200 }}>Código Transação</TableCell>
                                            <TableCell style={{ minWidth: 60 }} align="center">Ação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.transacoes.map((t, i) => (
                                            <TableRow key={i}>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={t.id}
                                                >
                                                    {t.id}
                                                </TableCell>
                                                <TableCell>{t.type ?? "-"}</TableCell>
                                                <TableCell>{t.status ?? "-"}</TableCell>
                                                <TableCell align="right">
                                                    <span className={classes.valorPositivo}>
                                                        R$ {t.total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{t.dataFormatada ?? "-"}</TableCell>
                                                <TableCell>{t.idPedido ?? "-"}</TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={t.codigoTransacao ?? ""}
                                                >
                                                    {t.codigoTransacao ?? "-"}
                                                </TableCell>
                                                <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                                    <IconButton
                                                        size="small"
                                                        title="Estornar"
                                                        onClick={() => handleEstornar(t)}
                                                    >
                                                        <Undo fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
